import React from 'react'
import { Button } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'
import type { MenuProps } from '@chakra-ui/react'
import type { ButtonProps } from '@chakra-ui/react'
import { Flex, Text, HStack } from '@chakra-ui/react'
import { Icon, ChevronDownIcon } from '@chakra-ui/icons'
import { FaArrowDown, FaArrowUp } from 'react-icons/fa6'
import { Menu, MenuList, MenuButton, MenuItem } from '@chakra-ui/react'

export type SortingType<T> = {
    field: keyof T
    isAscending: boolean
}

interface Props<T> extends Omit<ButtonProps, 'value'> {
    label?: string
    options: {
        label: string
        field: keyof T
        isAscending: boolean
        ascendingLabel?: React.ReactNode
    }[]
    value: SortingType<T>
    setValue: (value: SortingType<T>) => void
    menuOverrides?: Omit<MenuProps, 'children'>
}

export default function SortingSelect<T>({
    label = 'Sort By',
    options,
    value,
    setValue,
    menuOverrides,
    ...rest
}: Props<T>) {
    const selectedLabel = options.find(
        option =>
            option.field === value.field &&
            option.isAscending === value.isAscending,
    )?.label
    const selectedAscendingLabel = options.find(
        option =>
            option.field === value.field &&
            option.isAscending === value.isAscending,
    )?.ascendingLabel

    return (
        <Menu closeOnSelect={false} {...menuOverrides}>
            <Tooltip label={label} hasArrow>
                <MenuButton
                    as={Button}
                    size="sm"
                    rounded="md"
                    variant="outline"
                    colorScheme="gray"
                    rightIcon={<ChevronDownIcon />}
                    {...rest}
                >
                    <HStack>
                        <Text>{selectedLabel ?? label}</Text>
                        {selectedAscendingLabel ?? (
                            <Icon
                                as={value.isAscending ? FaArrowUp : FaArrowDown}
                                fontSize="xs"
                            />
                        )}
                    </HStack>
                </MenuButton>
            </Tooltip>
            <MenuList as="ul">
                {options.map(option => (
                    <MenuItem
                        key={`${option.field.toString()}-${option.isAscending}`}
                        fontSize="sm"
                        onClick={() =>
                            setValue({
                                field: option.field,
                                isAscending: option.isAscending,
                            })
                        }
                    >
                        <Flex
                            w="full"
                            flexDir="row"
                            justify="space-between"
                            align="center"
                        >
                            <Text fontWeight="medium">{option.label}</Text>
                            {option.ascendingLabel ?? (
                                <Icon
                                    as={
                                        option.isAscending
                                            ? FaArrowUp
                                            : FaArrowDown
                                    }
                                    fontSize="xs"
                                />
                            )}
                        </Flex>
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    )
}
